import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';

import {
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    map,
    Observable,
    startWith,
    switchMap,
} from 'rxjs';

import { AnalyticGroupData } from '@bazis/analytics/shared/models/analytic.types';

type BazisHorizontalPercentageBarsSettings = {
    mode: 'full' | 'short';
    hasSearch: boolean;
    searchPlaceholder: string;
};

@Component({
    selector: 'bazis-analytics-horizontal-percentage-bars',
    templateUrl: './horizontal-percentage-bars.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BazisHorizontalPercentageBarsComponent {
    readonly settings = input<BazisHorizontalPercentageBarsSettings>({
        mode: 'full',
        hasSearch: false,
        searchPlaceholder: '',
    });

    readonly groupData = input<AnalyticGroupData[]>([]);
    private readonly groupData$ = toObservable(this.groupData);

    readonly selectedValues = input<string[]>([]);

    readonly select = output<string>();

    readonly mode = computed(() => this.settings()?.mode ?? 'full');

    readonly hasSearch = computed(() => !!this.settings()?.hasSearch);
    private readonly hasSearch$ = toObservable(this.hasSearch);

    readonly search = new FormControl('');

    readonly searchPlaceholder = computed(() => this.settings()?.searchPlaceholder ?? '');

    private readonly searchChanges$ = this.search.valueChanges.pipe(
        startWith(this.search.value),
        debounceTime(200),
        distinctUntilChanged(),
    );

    readonly filteredGroupData$: Observable<AnalyticGroupData[]> = this.hasSearch$.pipe(
        switchMap((hasSearch) => {
            if (!hasSearch) return this.groupData$;

            return combineLatest([this.searchChanges$, this.groupData$]).pipe(
                map(([query, groupData = []]) => {
                    const q = query.trim().toLowerCase();

                    if (!q) return groupData;

                    return groupData.filter((i) => i?.name?.toLowerCase()?.includes(q));
                }),
            );
        }),
    );
}
